import { ColorPalette, OklchColor } from '../models/color-palette';

export class ColorUtils {
  private static readonly HEX_PATTERN = /^#[0-9a-f]{6}$/i;

  /**
   * Deltas derived from Tailwind's teal-700 -> teal-800/900.
   * Lightness and chroma use the 0–1 scale; hue uses degrees.
   */
  private static readonly HOVER_DELTA = {
    l: -0.074,
    c: -0.018,
    h: 1.825,
  };

  private static readonly ACTIVE_DELTA = {
    l: -0.125,
    c: -0.033,
    h: 2.025,
  };

  /**
   * Derived from the default teal-700 -> slate-400/blue-50
   * lightness and chroma differences. Hue is preserved so the
   * generated colors retain the user's chosen brand identity.
   */
  private static readonly PALE_DELTA = {
    l: 0.193,
    c: -0.056,
  };

  private static readonly SUBTLE_DELTA = {
    l: 0.459,
    c: -0.082,
  };

  /**
   * Creates the complete primary palette from one #RRGGBB color.
   */
  public static createPrimaryPalette(color: string): ColorPalette {
    const primary = this.normalizeHex(color);
    const base = this.hexToOklch(primary);

    return {
      primary,
      hover: this.createDarkVariant(base, this.HOVER_DELTA, 0.035),
      active: this.createDarkVariant(base, this.ACTIVE_DELTA, 0.065),
      pale: this.createLightVariant(base, this.PALE_DELTA, 0.035),
      subtle: this.createLightVariant(base, this.SUBTLE_DELTA, 0.02),
      foreground: this.calculateTextColorForBackground(primary),
    };
  }

  /**
   * Darkens a color using a reference delta.
   * Near black, a small lightness increase is used instead,
   * because further darkening would have no visible effect.
   */
  private static createDarkVariant(base: OklchColor, delta: { l: number; c: number; h: number }, fallbackLightness: number): string {
    let l = Math.max(0, base.l + delta.l);

    if (base.l - l < 0.025) {
      l = Math.min(1, base.l + fallbackLightness);
    }

    return this.oklchToHex({
      l,
      c: Math.max(0, base.c + delta.c),
      h: base.h + delta.h,
    });
  }

  /**
   * Lightens and desaturates a color.
   * Near white, slightly darkens it when further lightening
   * cannot produce a meaningful difference.
   */
  private static createLightVariant(base: OklchColor, delta: { l: number; c: number }, fallbackLightness: number): string {
    let l = Math.min(1, base.l + delta.l);

    if (l - base.l < 0.015) {
      l = Math.max(0, base.l - fallbackLightness);
    }

    return this.oklchToHex({
      l,
      c: Math.max(0, base.c + delta.c),
      h: base.h,
    });
  }

  /**
   * Chooses whichever of black and white has higher WCAG contrast.
   */
  public static calculateTextColorForBackground(color: string): string {
    const [r, g, b] = this.hexToRgb(color).map((v) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));

    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    return luminance > 0.1791287847 ? '#000000' : '#ffffff';
  }

  private static normalizeHex(color: string): string {
    if (!this.HEX_PATTERN.test(color)) {
      throw new Error(`Invalid hex color: ${color}. Expected #RRGGBB.`);
    }

    return color.toLowerCase();
  }

  private static hexToRgb(color: string): number[] {
    const hex = this.normalizeHex(color).slice(1);

    return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  }

  /** Converts sRGB to OKLCH. */
  private static hexToOklch(color: string): OklchColor {
    const [r, g, b] = this.hexToRgb(color).map((v) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));

    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

    const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
    const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
    const b2 = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

    return {
      l: L,
      c: Math.hypot(a, b2),
      h: (Math.atan2(b2, a) * 180) / Math.PI,
    };
  }

  /** Converts OKLCH to sRGB hex, reducing chroma when out of gamut. */
  private static oklchToHex(color: OklchColor): string {
    const { l, c, h } = color;

    if (l <= 0) return '#000000';
    if (l >= 1 && c === 0) return '#ffffff';

    const radians = (h * Math.PI) / 180;
    const a = c * Math.cos(radians);
    const b = c * Math.sin(radians);

    const toLinearRgb = (chromaScale: number): number[] => {
      const aa = a * chromaScale;
      const bb = b * chromaScale;

      const L = (l + 0.3963377774 * aa + 0.2158037573 * bb) ** 3;
      const M = (l - 0.1055613458 * aa - 0.0638541728 * bb) ** 3;
      const S = (l - 0.0894841775 * aa - 1.291485548 * bb) ** 3;

      return [
        4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S,
        -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S,
        -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S,
      ];
    };

    const inGamut = (rgb: number[]) => rgb.every((v) => Number.isFinite(v) && v >= 0 && v <= 1);

    let rgb = toLinearRgb(1);

    if (!inGamut(rgb)) {
      let low = 0;
      let high = 1;

      // Binary search for the maximum chroma that fits in sRGB.
      for (let i = 0; i < 24; i++) {
        const mid = (low + high) / 2;

        if (inGamut(toLinearRgb(mid))) {
          low = mid;
        } else {
          high = mid;
        }
      }

      rgb = toLinearRgb(low);
    }

    const toSrgb = (v: number) => (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);

    return (
      '#' +
      rgb
        .map((v) =>
          Math.round(Math.max(0, Math.min(1, toSrgb(v))) * 255)
            .toString(16)
            .padStart(2, '0'),
        )
        .join('')
    );
  }
}
